import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Activity, ArrowRight, Database, Images, KeyRound, LayoutDashboard, LoaderCircle, LogOut, Menu, MessageSquareText, RefreshCw, Search, ShieldCheck, ShoppingCart, Star, Users, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, ApiClientError } from '../services/api';
import { FunnelChart, RatingBars, TrendChart } from '../components/admin/Charts';
import { AdminGallery } from '../components/admin/AdminGallery';

function toFa(num: number | string): string {
  return String(num).replace(/[0-9]/g, (w) => '۰۱۲۳۴۵۶۷۸۹'[+w]);
}

const tabs = [
  { id: 'overview', label: 'نمای کلی', icon: LayoutDashboard },
  { id: 'gallery', label: 'گالری خروجی‌ها', icon: Images },
  { id: 'designs', label: 'طراحی‌ها', icon: Images },
  { id: 'sessions', label: 'جلسات و کاربران', icon: Users },
  { id: 'feedback', label: 'نظرات', icon: MessageSquareText },
  { id: 'logs', label: 'لاگ و مصرف', icon: Activity },
  { id: 'catalog', label: 'کاتالوگ', icon: Database },
];

function AdminSearch({
  value,
  onChange,
  placeholder,
  className = '',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
}) {
  return (
    <div className={`admin-search ${className}`.trim()}>
      <Search className="admin-search-icon" aria-hidden="true" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
    </div>
  );
}

export function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem('karolight-admin-token'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const [query, setQuery] = useState('');
  const [designQuery, setDesignQuery] = useState('');
  const [debouncedDesignQuery, setDebouncedDesignQuery] = useState('');
  const [designPage, setDesignPage] = useState(1);
  const [galleryPage, setGalleryPage] = useState(1);
  const [designData, setDesignData] = useState<any>(null);
  const [galleryData, setGalleryData] = useState<any>(null);
  const [designsBusy, setDesignsBusy] = useState(false);
  const [designReload, setDesignReload] = useState(0);
  const [logAction, setLogAction] = useState('ALL');
  const [logOutcome, setLogOutcome] = useState('ALL');
  // منوی کشویی موبایل: سایدبار فقط در عرض کم با دکمه‌ی سه‌خط باز می‌شود.
  // سایدبار و پس‌زمینه در موبایل با Portal به body منتقل می‌شوند چون wrapper پنل
  // با isolation یک stacking context می‌سازد و دراور را زیر هدر چسبان سایت می‌برد.
  const [navOpen, setNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 720px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 720px)');
    const onChange = () => { setIsMobile(query.matches); if (!query.matches) setNavOpen(false); };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    if (isMobile) document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow; };
  }, [navOpen, isMobile]);

  const load = async (auth = token) => {
    if (!auth) return;
    setBusy(true); setError('');
    try { setData(await api.adminDashboard(auth)); }
    catch (e) {
      setError(messageOf(e));
      if (e instanceof ApiClientError && e.status === 401) { sessionStorage.removeItem('karolight-admin-token'); setToken(null); }
    } finally { setBusy(false); }
  };
  useEffect(() => { void load(); }, [token]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedDesignQuery(designQuery.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [designQuery]);

  useEffect(() => {
    if (!token || (tab !== 'designs' && tab !== 'gallery')) return;
    let cancelled = false;
    const page = tab === 'designs' ? designPage : galleryPage;
    const pageSize = tab === 'designs' ? 12 : 18;
    const search = tab === 'designs' ? debouncedDesignQuery : '';
    setDesignsBusy(true);
    void api.adminDesigns(token, page, pageSize, search)
      .then((result) => {
        if (cancelled) return;
        if (tab === 'designs') setDesignData(result);
        else setGalleryData(result);
      })
      .catch((caught) => {
        if (cancelled) return;
        setError(messageOf(caught));
        if (caught instanceof ApiClientError && caught.status === 401) {
          sessionStorage.removeItem('karolight-admin-token');
          setToken(null);
        }
      })
      .finally(() => { if (!cancelled) setDesignsBusy(false); });
    return () => { cancelled = true; };
  }, [debouncedDesignQuery, designPage, designReload, galleryPage, tab, token]);

  const login = async () => {
    setBusy(true); setError('');
    try { const result = await api.adminLogin(username, password); sessionStorage.setItem('karolight-admin-token', result.token); setToken(result.token); }
    catch (e) { setError(messageOf(e)); }
    finally { setBusy(false); }
  };
  const sync = async () => {
    if (!token) return;
    setBusy(true); setError('');
    try { await api.syncCatalog(token); await load(token); }
    catch (e) { setError(messageOf(e)); }
    finally { setBusy(false); }
  };

  const filteredSessions = useMemo(() => data?.sessions?.filter((item: any) => `${item.id} ${item.user?.name || ''} ${item.user?.phone || ''}`.includes(query)) || [], [data, query]);
  const logActions = useMemo(() => [...new Set<string>((data?.audits || []).map((item: any) => item.action))], [data]);
  const filteredAudits = useMemo(() => (data?.audits || []).filter((item: any) => (logAction === 'ALL' || item.action === logAction) && (logOutcome === 'ALL' || item.outcome === logOutcome)), [data, logAction, logOutcome]);

  // درصد تغییر هفت روز اخیر نسبت به هفته قبل از داده‌ی روند
  const trendDelta = (values: number[] = []): number | null => {
    if (values.length < 14) return null;
    const recent = values.slice(-7).reduce((sum, value) => sum + value, 0);
    const previous = values.slice(-14, -7).reduce((sum, value) => sum + value, 0);
    if (!previous) return recent ? 100 : null;
    return Math.round(((recent - previous) / previous) * 100);
  };
  const isToday = (value: string) => new Date(value).toDateString() === new Date().toDateString();
  const failuresToday = useMemo(() => (data?.audits || []).filter((item: any) => isToday(item.createdAt) && item.outcome === 'FAILURE').length, [data]);
  const generationsToday = data?.trends?.generations?.at(-1) ?? 0;
  const cartsToday = useMemo(() => (data?.cartActions || []).filter((item: any) => isToday(item.createdAt)).length, [data]);
  const lastSync = data?.syncLogs?.[0] ?? null;
  const topUsers = useMemo(() => {
    const map = new Map<string, { name: string; phone: string; designs: number; carts: number }>();
    for (const generation of data?.recentGenerations ?? []) {
      const user = generation.session?.user;
      const key = user?.id || generation.session?.id || generation.id;
      const entry = map.get(key) ?? { name: user?.name || 'کاربر مهمان', phone: user?.phone || '', designs: 0, carts: 0 };
      entry.designs += 1;
      entry.carts += generation.cartActions?.length ?? 0;
      map.set(key, entry);
    }
    return [...map.values()].sort((a, b) => b.designs - a.designs || b.carts - a.carts).slice(0, 5);
  }, [data]);
  const topProductMax = Math.max(1, ...(data?.topCartProducts || []).map((item: any) => item.count));

  if (!token) return (
    <div className="admin-scale-wrapper">
      <main className="admin-login">
        <section>
          <span className="admin-lock"><ShieldCheck /></span>
          <span className="eyebrow">دسترسی حفاظت‌شده</span>
          <h1>ورود به پنل مدیریت</h1>
          <p>برای مشاهده جلسات، خروجی‌ها، نظرات و وضعیت همگام‌سازی وارد شوید.</p>
          {error && <div className="notice error"><span>{error}</span></div>}
          <label className="field">
            <span>نام کاربری</span>
            <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
          </label>
          <label className="field">
            <span>رمز عبور</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void login()} autoComplete="current-password" />
          </label>
          <button className="primary-button wide" disabled={busy || !username || password.length < 8} onClick={() => void login()}>
            {busy ? <><LoaderCircle className="spin" />در حال ورود…</> : <><KeyRound />ورود امن</>}
          </button>
          <Link className="admin-home-link" to="/"><ArrowRight />بازگشت به صفحه اصلی</Link>
        </section>
      </main>
    </div>
  );

  const adminSidebar = (
    <aside className={`admin-sidebar${navOpen ? ' open' : ''}`}>
      <div className="admin-title">
        <span><ShieldCheck /></span>
        <div>
          <strong>مدیریت کارو لایت</strong>
          <small>مرکز پایش طراح هوشمند</small>
        </div>
        <button type="button" className="admin-nav-close" onClick={() => setNavOpen(false)} aria-label="بستن منو">
          <X size={18} />
        </button>
      </div>
      <nav>
        {tabs.map((item) => (
          <button key={item.id} className={tab === item.id ? 'active' : ''} onClick={() => { setTab(item.id); setNavOpen(false); }}>
            <item.icon />{item.label}
          </button>
        ))}
      </nav>
      <button className="logout" onClick={() => { sessionStorage.removeItem('karolight-admin-token'); setToken(null); setNavOpen(false); }}>
        <LogOut />خروج از پنل
      </button>
    </aside>
  );

  return (
    <div className="admin-scale-wrapper">
      <main className="admin-shell">
        <div className="admin-mobile-bar">
          <button type="button" className="admin-burger" onClick={() => setNavOpen(true)} aria-label="باز کردن منوی مدیریت">
            <Menu size={20} />
          </button>
          <strong>مدیریت کارو لایت</strong>
          <span className="admin-mobile-tab">{tabs.find((item) => item.id === tab)?.label}</span>
        </div>
        {isMobile
          ? createPortal(<>{navOpen && <div className="admin-backdrop" onClick={() => setNavOpen(false)} />}{adminSidebar}</>, document.body)
          : adminSidebar}
        <section className="admin-content">
          <header>
            <div>
              <span className="eyebrow">گزارش زنده سامانه</span>
              <h1>{tabs.find((item) => item.id === tab)?.label}</h1>
            </div>
            <button className="secondary-button" disabled={busy || designsBusy} onClick={() => { void load(); setDesignReload((value) => value + 1); }}>
              <RefreshCw className={busy || designsBusy ? 'spin' : ''} />به‌روزرسانی
            </button>
          </header>
          {error && <div className="notice error"><span>{error}</span></div>}
          {!data ? (
            <div className="admin-loading"><LoaderCircle className="spin" />در حال دریافت داده‌ها…</div>
          ) : (
            <>
              {tab === 'overview' && (
                <>
                  <div className="stats-grid">
                    <Stat icon={Users} label="کاربران" value={toFa(data.stats.users)} tone="violet" />
                    <Stat icon={Activity} label="جلسات" value={toFa(data.stats.sessions)} tone="blue" delta={trendDelta(data.trends?.sessions)} />
                    <Stat icon={Images} label="تولید تصاویر" value={toFa(data.stats.generations)} tone="brand" delta={trendDelta(data.trends?.generations)} />
                    <Stat icon={Star} label="میانگین امتیاز" value={`${toFa(Number(data.stats.averageRating).toFixed(1))} از ۵`} tone="gold" />
                    <Stat icon={Database} label="محصولات موجود" value={`${toFa(data.stats.inStockProducts)}/${toFa(data.stats.products)}`} tone="teal" />
                    <Stat icon={ShoppingCart} label="اقدام افزودن به سبد" value={toFa(data.stats.cartActions)} tone="green" delta={trendDelta(data.trends?.cartActions)} />
                    <Stat icon={PackageCartIcon} label="محصول افزوده‌شده" value={toFa(data.stats.cartProducts)} tone="amber" />
                  </div>
                  <div className="system-status-row">
                    <div className={`system-pill ai-service-status ${failuresToday ? 'warn' : 'ok'}`}>
                      <i />سرویس هوش مصنوعی: {failuresToday ? `${toFa(failuresToday)} خطا امروز` : 'بدون خطا در امروز'}
                    </div>
                    <div className={`system-pill ${lastSync && lastSync.status === 'FAILED' ? 'warn' : 'ok'}`}>
                      <i />کاتالوگ فروشگاه: {lastSync ? `${statusLabel(lastSync.status)} · ${date(lastSync.startedAt)}` : 'هنوز همگام نشده'}
                    </div>
                    <div className="system-pill ok"><i />تولید امروز: {toFa(generationsToday)}</div>
                    <div className="system-pill ok"><i />افزودن به سبد امروز: {toFa(cartsToday)}</div>
                  </div>
                  <div className="admin-panels">
                    <Panel title="روند ۱۴ روز اخیر" wide>
                      <TrendChart days={data.trends?.days ?? []} series={[
                        { label: 'جلسات', color: '#7c9cd6', values: data.trends?.sessions ?? [] },
                        { label: 'تولید تصویر', color: '#FFB703', values: data.trends?.generations ?? [] },
                        { label: 'افزودن به سبد', color: '#23805c', values: data.trends?.cartActions ?? [] },
                      ]} />
                    </Panel>
                    <Panel title="توزیع امتیاز نظرسنجی‌ها">
                      <RatingBars distribution={data.ratingDistribution ?? {}} />
                    </Panel>
                    <Panel title="قیف تبدیل طراحی تا خرید">
                      <FunnelChart stages={[
                        { label: 'جلسات طراحی', value: data.stats.sessions, color: '#7c9cd6' },
                        { label: 'تولید تصویر', value: data.stats.generations, color: '#FFB703' },
                        { label: 'اقدام افزودن به سبد', value: data.stats.cartActions, color: '#23805c' },
                      ]} />
                    </Panel>
                    <Panel title="محصولات پرانتخاب برای سبد">
                      {(data.topCartProducts || []).length ? <div className="top-product-ranking">
                        {(data.topCartProducts || []).map((item: any, index: number) => (
                          <div key={item.id} className="rank-row">
                            <span className="rank-badge">{toFa(index + 1)}</span>
                            <a className="rank-name" href={item.storeUrl} target="_blank" rel="noreferrer">{item.name}</a>
                            <span className="rank-track"><i style={{ width: `${(item.count / topProductMax) * 100}%` }} /></span>
                            <b className="rank-count">{toFa(item.count)} بار</b>
                          </div>
                        ))}
                      </div> : <div className="empty-admin">هنوز محصولی به سبد اضافه نشده است.</div>}
                    </Panel>
                    <Panel title="فعال‌ترین کاربران اخیر">
                      {topUsers.length ? <div className="top-user-ranking">
                        {topUsers.map((user, index) => (
                          <div key={`${user.name}-${index}`} className="rank-row user">
                            <span className="rank-badge">{toFa(index + 1)}</span>
                            <span className="rank-name">{user.name}{user.phone ? <small>{toFa(user.phone)}</small> : null}</span>
                            <b className="rank-count">{toFa(user.designs)} طراحی · {toFa(user.carts)} سبد</b>
                          </div>
                        ))}
                      </div> : <div className="empty-admin">کاربری ثبت نشده است.</div>}
                    </Panel>
                  </div>
                  <div className="admin-panels">
                    <Panel title="آخرین تولیدها" wide>
                      <DataTable className="latest-generations-table" headers={['کاربر', 'وضعیت', 'پردازش', 'زمان']} rows={(data.recentGenerations || []).map((g: any) => [g.session?.user?.name || '—', statusLabel(g.status), g.imageModel || g.analysisModel || 'هوش مصنوعی', date(g.createdAt)])} />
                    </Panel>
                    <Panel title="آخرین نظرات">
                      <DataTable headers={['کاربر', 'امتیاز', 'نظر']} rows={data.surveys.slice(0, 8).map((s: any) => [s.user?.name || '—', `${toFa(s.rating)} از ۵`, s.comment || 'بدون متن'])} />
                    </Panel>
                    <Panel title="آخرین اقدام‌های سبد خرید">
                      <DataTable className="latest-cart-table" headers={['کاربر', 'محصولات', 'زمان']} rows={(data.cartActions || []).slice(0, 10).map((item: any) => [item.user?.name || item.user?.phone || '—', item.products.map((product: any) => product.name).join('، '), date(item.createdAt)])} />
                    </Panel>
                  </div>
                </>
              )}
              {tab === 'gallery' && (
                <>
                  <div className="admin-result-count">{galleryData ? `${toFa(galleryData.pagination.total)} طراحی در آرشیو` : 'در حال دریافت آرشیو…'}</div>
                  {designsBusy && !galleryData
                    ? <div className="admin-loading"><LoaderCircle className="spin" />در حال دریافت طراحی‌ها…</div>
                    : <AdminGallery generations={galleryData?.items ?? []} />}
                  {galleryData && <AdminPagination pagination={galleryData.pagination} busy={designsBusy} onPage={setGalleryPage} />}
                </>
              )}
              {tab === 'designs' && (
                <>
                  <AdminSearch
                    className="design-search"
                    value={designQuery}
                    onChange={(value) => { setDesignQuery(value); setDesignPage(1); }}
                    placeholder="جستجو با نام، موبایل، شناسه یا متن مکالمه…"
                  />
                  <div className="admin-result-count">{designData ? `${toFa(designData.pagination.total)} طراحی پیدا شد` : 'در حال دریافت طراحی‌ها…'}</div>
                  <div className="generation-admin-list">
                    {designsBusy && !designData ? <div className="admin-loading"><LoaderCircle className="spin" />در حال دریافت طراحی‌ها…</div> : designData?.items?.length ? designData.items.map((generation: any) => (
                      <GenerationAdminCard key={generation.id} generation={generation} />
                    )) : <div className="empty-admin">طراحی‌ای برای نمایش وجود ندارد.</div>}
                  </div>
                  {designData && <AdminPagination pagination={designData.pagination} busy={designsBusy} onPage={setDesignPage} />}
                </>
              )}
              {tab === 'sessions' && (
                <>
                  <AdminSearch
                    value={query}
                    onChange={setQuery}
                    placeholder="جستجو با نام، موبایل یا شناسه جلسه…"
                  />
                  <Panel title="جلسات اخیر">
                    <DataTable headers={['شناسه', 'کاربر', 'موبایل', 'وضعیت', 'مرحله', 'تاریخ']} rows={filteredSessions.map((s: any) => [s.id.slice(0, 8), s.user?.name || 'مهمان', s.user?.phone || '—', statusLabel(s.status), s.currentStep, date(s.createdAt)])} />
                  </Panel>
                </>
              )}
              {tab === 'feedback' && (
                <Panel title="امتیاز و نظر کاربران">
                  <DataTable headers={['کاربر', 'امتیاز', 'نظر', 'برچسب‌ها', 'زمان']} rows={data.surveys.map((s: any) => [s.user?.name || '—', `${toFa(s.rating)} ⭐`, s.comment || 'بدون متن', Array.isArray(s.tags) ? s.tags.join('، ') : '—', date(s.createdAt)])} />
                </Panel>
              )}
              {tab === 'logs' && (
                <div className="admin-panels vertical">
                  <div className="audit-filters">
                    <label>
                      نوع رویداد
                      <select value={logAction} onChange={(e) => setLogAction(e.target.value)}>
                        <option value="ALL">همه رویدادهای ضروری</option>
                        {logActions.map((action) => <option key={action} value={action}>{auditLabel(action)}</option>)}
                      </select>
                    </label>
                    <label>
                      نتیجه
                      <select value={logOutcome} onChange={(e) => setLogOutcome(e.target.value)}>
                        <option value="ALL">همه نتایج</option>
                        <option value="SUCCESS">موفق</option>
                        <option value="FAILURE">ناموفق</option>
                      </select>
                    </label>
                    <small>متن مکالمات در تب «طراحی‌ها» نگهداری می‌شود، اما برای جلوگیری از شلوغی، برای هر پیام یک Audit Log جداگانه ساخته نمی‌شود.</small>
                  </div>
                  <Panel title="رویدادهای ضروری">
                    <DataTable headers={['عملیات', 'عامل', 'نتیجه', 'زمان']} rows={filteredAudits.map((a: any) => [auditLabel(a.action), actorLabel(a.actorType), statusLabel(a.outcome), date(a.createdAt)])} />
                  </Panel>
                  <Panel title="تلاش‌های OTP اخیر">
                    <DataTable headers={['موبایل', 'وضعیت', 'تلاش', 'زمان']} rows={data.otpAttempts.map((o: any) => [o.phone, statusLabel(o.status), `${o.attempts}/${o.maxAttempts}`, date(o.createdAt)])} />
                  </Panel>
                  <Panel title="مصرف روزانه تولید">
                    <DataTable headers={['کاربر', 'روز', 'تعداد']} rows={data.usage.map((u: any) => [u.user?.name || u.user?.phone, u.dateKey, u.count])} />
                  </Panel>
                </div>
              )}
              {tab === 'catalog' && (
                <>
                  <div className="catalog-health">
                    <div>
                      <span className="eyebrow">منبع داده: فروشگاه کارو لایت</span>
                      <h2>{data.stats.products} محصول همگام‌شده</h2>
                      <p>بدون کلید WooCommerce از Store API عمومی استفاده می‌شود. پس از افزودن کلیدها، سرویس به‌صورت خودکار به REST API احراز‌شده تغییر می‌کند.</p>
                    </div>
                    <button className="primary-button" disabled={busy} onClick={() => void sync()}>
                      <RefreshCw className={busy ? 'spin' : ''} />همگام‌سازی اکنون
                    </button>
                  </div>
                  <Panel title="تاریخچه همگام‌سازی">
                    <DataTable headers={['حالت', 'وضعیت', 'پردازش', 'جدید', 'به‌روزشده', 'زمان']} rows={data.syncLogs.map((s: any) => [s.mode, statusLabel(s.status), s.processedCount, s.createdCount, s.updatedCount, date(s.startedAt)])} />
                  </Panel>
                </>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}

function AdminPagination({ pagination, busy, onPage }: { pagination: { page: number; totalPages: number; total: number }; busy: boolean; onPage: (page: number) => void }) {
  if (pagination.totalPages <= 1) return null;
  return (
    <nav className="admin-pagination" aria-label="صفحه‌بندی طراحی‌ها">
      <button className="secondary-button" disabled={busy || pagination.page <= 1} onClick={() => onPage(pagination.page - 1)}>صفحه قبل</button>
      <span>صفحه {toFa(pagination.page)} از {toFa(pagination.totalPages)}</span>
      <button className="secondary-button" disabled={busy || pagination.page >= pagination.totalPages} onClick={() => onPage(pagination.page + 1)}>صفحه بعد</button>
    </nav>
  );
}

function GenerationAdminCard({ generation }: { generation: any }) {
  const user = generation.session?.user;
  const messages = generation.conversationMessages || [];
  return (
    <article className="generation-admin-card">
      <header>
        <div>
          <span className={`generation-status ${String(generation.status).toLowerCase()}`}>{statusLabel(generation.status)}</span>
          <h2>{user?.name || 'کاربر مهمان'}</h2>
          <p>{user?.phone || 'بدون شماره'} · {date(generation.createdAt)}</p>
        </div>
        <code>{generation.id.slice(0, 8)}</code>
      </header>
      <div className="generation-models">
        {generation.analysisModel && <span><Images />مدل تحلیل <strong>{generation.analysisModel}</strong></span>}
        {generation.imageModel && <span><Images />مدل تولید تصویر <strong>{generation.imageModel}</strong></span>}
        {!generation.analysisModel && !generation.imageModel && <span><Images />پردازش طراحی <strong>هوش مصنوعی</strong></span>}
        {generation.processingMs && <span><Activity />زمان پردازش <strong>{Math.round(generation.processingMs / 1000)} ثانیه</strong></span>}
      </div>
      <div className="admin-before-after">
        <figure>
          {generation.beforeImageUrl ? (
            <a href={generation.beforeImageUrl} target="_blank" rel="noreferrer"><img src={generation.beforeImageUrl} alt="تصویر قبل کاربر" loading="lazy" /></a>
          ) : <div className="image-placeholder">تصویر اولیه موجود نیست</div>}
          <figcaption>قبل</figcaption>
        </figure>
        <figure>
          {generation.afterImageUrl ? (
            <a href={generation.afterImageUrl} target="_blank" rel="noreferrer"><img src={generation.afterImageUrl} alt="تصویر تولیدشده کاربر" loading="lazy" /></a>
          ) : <div className="image-placeholder">{generation.status === 'FAILED' ? 'تولید ناموفق' : 'هنوز آماده نشده'}</div>}
          <figcaption>بعد</figcaption>
        </figure>
      </div>
      {generation.products?.length > 0 && (
        <div className="admin-product-chips">
          {generation.products.map((product: any) => <a key={product.id} href={product.storeUrl} target="_blank" rel="noreferrer">{product.name}</a>)}
        </div>
      )}
      {generation.cartActions?.length > 0 && <div className="admin-cart-actions"><header><ShoppingCart /><strong>این طراحی به افزودن محصول به سبد منجر شده است</strong><span>{toFa(generation.cartActions.length)} اقدام</span></header>{generation.cartActions.map((action: any) => <div key={action.id}><time>{date(action.createdAt)}</time><span>{action.products.map((product: any) => product.name).join('، ')}</span></div>)}</div>}
      {generation.errorMessage && <div className="generation-error">{generation.errorMessage}</div>}
      <ConversationDetails messages={messages} />
      <details className="prompt-details">
        <summary>پرامپت نهایی تولیدشده توسط هوش مصنوعی</summary>
        <div>
          <pre dir="auto">{generation.analysisPrompt || generation.prompt || 'پرامپتی ثبت نشده است.'}</pre>
        </div>
      </details>
      <details className="prompt-details">
        <summary>پرامپت کامل استفاده‌شده برای تولید تصویر</summary>
        <div><pre dir="auto">{generation.prompt || 'پرامپتی ثبت نشده است.'}</pre></div>
      </details>
    </article>
  );
}

function ConversationDetails({ messages }: { messages: Array<{ id: string; role: string; content: string; createdAt: string }> }) {
  return (
    <details className="conversation-details">
      <summary>
        <span><MessageSquareText />مکالمه کاربر با مشاور هوشمند</span>
        <b>{messages.length} پیام</b>
      </summary>
      <div className="admin-conversation">
        {messages.length ? messages.map((message) => (
          <article key={message.id} className={`admin-message ${message.role === 'assistant' ? 'assistant' : 'user'}`}>
            <header>
              <strong>{message.role === 'assistant' ? 'مشاور هوشمند' : 'کاربر'}</strong>
              <time>{date(message.createdAt)}</time>
            </header>
            <p dir="auto">{message.content}</p>
          </article>
        )) : <div className="empty-conversation">برای این طراحی مکالمه‌ای ثبت نشده است.</div>}
      </div>
    </details>
  );
}

function Stat({ icon: Icon, label, value, delta, tone = 'brand' }: { icon: any; label: string; value: any; delta?: number | null; tone?: string }) {
  return (
    <article className={`stat-card tone-${tone}`}>
      <span className="stat-icon"><Icon /></span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        {delta !== null && delta !== undefined && (
          <em className={delta >= 0 ? 'delta up' : 'delta down'}>
            {delta >= 0 ? '▲' : '▼'} {toFa(Math.abs(delta))}٪ نسبت به هفته قبل
          </em>
        )}
      </div>
    </article>
  );
}

function PackageCartIcon() { return <ShoppingCart />; }

function Panel({ title, children, wide = false }: { title: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <section className={`admin-panel${wide ? ' wide' : ''}`}>
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function DataTable({ headers, rows, className = '' }: { headers: string[]; rows: any[][]; className?: string }) {
  return (
    <div className={`table-wrap ${className}`.trim()}>
      <table>
        <thead>
          <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row, index) => (
            <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{String(cell ?? '—')}</td>)}</tr>
          )) : <tr><td colSpan={headers.length}>داده‌ای برای نمایش وجود ندارد.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function date(value: string) { return new Date(value).toLocaleString('fa-IR', { dateStyle: 'short', timeStyle: 'short' }); }
function statusLabel(value: string) { return ({ ACTIVE: 'فعال', TEMP: 'موقت', OTP_PENDING: 'در انتظار OTP', COMPLETED: 'تکمیل‌شده', FAILED: 'ناموفق', PROCESSING: 'در حال پردازش', SUCCESS: 'موفق', RUNNING: 'در حال اجرا', VERIFIED: 'تأییدشده', PENDING: 'در انتظار' } as Record<string, string>)[value] || value; }
function actorLabel(value: string) { return ({ USER: 'کاربر', ADMIN: 'مدیر', AI: 'هوش مصنوعی', SYSTEM: 'سامانه' } as Record<string, string>)[value] || value; }
function auditLabel(value: string) { return ({ ADMIN_LOGIN: 'ورود مدیر', ROOM_UPLOADED: 'بارگذاری اتاق', OTP_VERIFIED: 'تأیید هویت', REQUIREMENTS_CONFIRMED: 'تأیید نیازمندی', RECOMMENDATION_REMOVED: 'حذف محصول پیشنهادی', GENERATION_REQUESTED: 'درخواست تولید', GENERATION_COMPLETED: 'تولید موفق', GENERATION_FAILED: 'تولید ناموفق', SURVEY_SUBMITTED: 'ثبت نظر', CART_PRODUCTS_SELECTED: 'انتخاب محصول برای سبد', CATALOG_SYNCED: 'همگام‌سازی کاتالوگ' } as Record<string, string>)[value] || value; }
function messageOf(error: unknown) { return error instanceof Error ? error.message : 'خطای ناشناخته'; }
